import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import stylesGenerator from './styles';
import withStyles from '../withStyles';
import Label from '../Label';
import { formElementPropTypes, formElementDefaultProps } from '../propTypes';
import { sanitizeHtml, textToHtml } from './utils';

const TOOLBAR_BUTTONS = [
  { command: 'bold', title: 'Bold (Ctrl+B)', label: <strong>B</strong> },
  { command: 'italic', title: 'Italic (Ctrl+I)', label: <em>I</em> },
  { command: 'underline', title: 'Underline (Ctrl+U)', label: <u>U</u> },
  { command: 'strikeThrough', title: 'Strikethrough', label: <s>S</s> },
  { command: 'insertUnorderedList', title: 'Bulleted list', label: '• List' },
  { command: 'insertOrderedList', title: 'Numbered list', label: '1. List' },
  { command: 'removeFormat', title: 'Clear formatting', label: 'Clear' }
];

const BLOCK_NODE = /^(P|H[1-6]|UL|OL|BLOCKQUOTE|PRE|TABLE|HR)$/;

/**
 * Lightweight rich text editor (no external dependency).
 * Emits sanitized HTML through `onChange`, e.g.
 * `<p>This is <strong>bold</strong> and <em>italic</em> text.</p>`
 * Tables pasted from Excel, Outlook, Word or web pages are kept as tables.
 */
class RichTextEditor extends PureComponent {
  static propTypes = {
    ...formElementPropTypes,
    value: PropTypes.string,
    placeholder: PropTypes.string
  };

  static defaultProps = {
    ...formElementDefaultProps,
    styles: {},
    value: '',
    placeholder: ''
  };

  state = { activeCommands: {} };

  componentDidMount() {
    this.lastEmitted = this.props.value || '';
    this.editor.innerHTML = this.lastEmitted;
    document.addEventListener('selectionchange', this.updateActiveCommands);
  }

  componentDidUpdate() {
    // Only overwrite the DOM when the value was changed from outside (e.g. reset),
    // otherwise the caret position would be lost while typing.
    const { value } = this.props;
    if ((value || '') !== this.lastEmitted) {
      this.lastEmitted = value || '';
      this.editor.innerHTML = this.lastEmitted;
    }
  }

  componentWillUnmount() {
    document.removeEventListener('selectionchange', this.updateActiveCommands);
  }

  emitChange = () => {
    const html = sanitizeHtml(this.editor.innerHTML);
    if (html !== this.lastEmitted) {
      this.lastEmitted = html;
      this.props.onChange(html);
    }
  };

  updateActiveCommands = () => {
    if (!this.editor || !this.editor.contains(document.activeElement)) return;
    const activeCommands = {};
    TOOLBAR_BUTTONS.forEach(({ command }) => {
      try {
        activeCommands[command] = document.queryCommandState(command);
      } catch (e) {
        activeCommands[command] = false;
      }
    });
    this.setState({ activeCommands });
  };

  handleCommand = command => e => {
    e.preventDefault(); // keep focus/selection inside the editor
    this.editor.focus();
    document.execCommand(command, false, null);
    this.emitChange();
    this.updateActiveCommands();
  };

  handleFocus = () => {
    // New lines become <p> instead of <div> in Chromium/Firefox
    document.execCommand('defaultParagraphSeparator', false, 'p');
  };

  handlePaste = e => {
    const clipboard = e.clipboardData || window.clipboardData;
    if (!clipboard) return; // let the browser handle it, emitChange cleans up
    e.preventDefault();

    const html = clipboard.getData('text/html');
    const content = html
      ? sanitizeHtml(html)
      : textToHtml(
          clipboard.getData('text/plain') || clipboard.getData('Text')
        );

    if (content) this.insertHtmlAtCaret(content);
    this.emitChange();
  };

  /**
   * Inserts HTML at the caret as-is. `execCommand('insertHTML')` is avoided
   * because browsers rewrite the markup (flattening tables, dropping styles).
   */
  insertHtmlAtCaret = html => {
    const { editor } = this;
    const selection = window.getSelection();
    if (
      !selection.rangeCount ||
      !editor.contains(selection.getRangeAt(0).commonAncestorContainer)
    ) {
      editor.insertAdjacentHTML('beforeend', html);
      return;
    }

    const range = selection.getRangeAt(0);
    range.deleteContents();
    let fragment = range.createContextualFragment(html);

    // A single unstyled paragraph is pasted inline, without breaking the line
    const only = fragment.firstChild;
    if (
      fragment.childNodes.length === 1 &&
      only.nodeName === 'P' &&
      !only.getAttribute('style') &&
      only.childNodes.length
    ) {
      fragment = document.createDocumentFragment();
      while (only.firstChild) fragment.appendChild(only.firstChild);
    }

    const lastNode = fragment.lastChild;
    const hasBlocks = Array.prototype.some.call(
      fragment.childNodes,
      node => node.nodeType === 1 && BLOCK_NODE.test(node.nodeName)
    );

    // Find the top-level block holding the caret
    let block = range.startContainer;
    while (block && block.parentNode !== editor) block = block.parentNode;

    if (hasBlocks && block) {
      // Pasting blocks (e.g. a table) inside a paragraph: split it at the caret
      const tail = document.createRange();
      tail.setStart(range.startContainer, range.startOffset);
      tail.setEndAfter(block);
      const rest = tail.extractContents(); // text after the caret
      const next = block.nextSibling;
      editor.insertBefore(fragment, next);
      if (rest.textContent.trim()) editor.insertBefore(rest, next);
      if (!block.textContent.trim() && !block.querySelector('table, img')) {
        editor.removeChild(block);
      }
    } else if (hasBlocks) {
      editor.appendChild(fragment);
    } else {
      range.insertNode(fragment);
    }

    // Move the caret to the end of the pasted content
    const caret = document.createRange();
    caret.selectNodeContents(lastNode);
    caret.collapse(false);
    selection.removeAllRanges();
    selection.addRange(caret);
  };

  handleBlur = () => {
    this.emitChange();
    this.props.onBlur(this.lastEmitted);
  };

  render() {
    const { id, disabled, placeholder, computedStyles } = this.props;
    const { activeCommands } = this.state;

    return (
      <div className={computedStyles.base} role="group">
        <Label {...this.props} />
        <div className={computedStyles.toolbar} role="toolbar">
          {TOOLBAR_BUTTONS.map(({ command, title, label }) => (
            <button
              key={command}
              type="button"
              title={title}
              aria-label={title}
              aria-pressed={!!activeCommands[command]}
              disabled={disabled}
              className={`${computedStyles.toolbarButton} ${
                activeCommands[command] ? 'active' : ''
              }`}
              onMouseDown={this.handleCommand(command)}
            >
              {label}
            </button>
          ))}
        </div>
        <div
          id={id}
          ref={el => (this.editor = el)}
          className={`pt-input ${computedStyles.input} ${
            computedStyles.editor
          }`}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-labelledby={`label-${id}`}
          data-placeholder={placeholder}
          onInput={this.emitChange}
          onPaste={this.handlePaste}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          onKeyUp={this.updateActiveCommands}
          onMouseUp={this.updateActiveCommands}
        />
      </div>
    );
  }
}

export default withStyles(stylesGenerator)(RichTextEditor);
